// An issue's parent and its sub-issues, as today's issue page shows them: the
// parent in the property column and the breadcrumb, and a Sub-issues section
// listing the issues that name it, each with its state, and how many are done.
// Unlike today's page, a person sets and clears the parent here (`set_parent`),
// through the store's outbox, so it shows at once and waits out a lost
// connection. The server refuses a parent of another project, a loop and a
// chain too deep; the store then rolls the change back and says so.
import { useId, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import type { Issue } from '../api/types'
import { CloseIcon, PriorityIcon, StateIcon } from '../components/Icons'
import { sameKey } from '../lib/keys'
import { issuePath } from '../plans/html'
import { useBoardState, useStore } from '../store/context'

/** The sub-issues of an issue, by number: the issues that name it as their parent. */
export function childrenOf(issues: ReadonlyMap<string, Issue>, id: string): Issue[] {
  return [...issues.values()].filter((i) => i.parent === id).sort((a, b) => a.number - b.number)
}

/** How many of the issues are finished, as today's page counts them: done, cancelled or a duplicate. */
export function progressOf(children: readonly Issue[]): { done: number; total: number } {
  return { done: children.filter((c) => c.state === 'done' || c.state === 'cancelled' || c.state === 'duplicate').length, total: children.length }
}

/** The parent as a property: its line with a way to clear it, or a way to set one. */
export function IssueParent({ issue }: { issue: Issue }) {
  const store = useStore()
  const { issues } = useBoardState()
  const [setting, setSetting] = useState(false)
  const parent = issue.parent ? issues.get(issue.parent) : undefined
  return (
    <div className="prop">
      <dt>Parent</dt>
      <dd className="prop-list">
        {issue.parent && (
          <span className="parent-line">
            {parent ? (
              <Link to={issuePath(parent)} className="prop-link" title={parent.title}>
                <StateIcon state={parent.state} />
                <span className="issue-line-key">{parent.key}</span> {parent.title}
              </Link>
            ) : (
              <span className="muted">Another issue</span>
            )}
            <button type="button" className="relation-remove" aria-label="Clear the parent" title="Clear the parent" onClick={() => void store.setParent(issue.id, null)}>
              <CloseIcon size={12} />
            </button>
          </span>
        )}
        {!issue.parent && !setting && <span className="muted">No parent</span>}
        {!setting && (
          <button type="button" className="button button-small button-quiet" onClick={() => setSetting(true)}>
            {issue.parent ? 'Change parent' : 'Set parent'}
          </button>
        )}
        {setting && <SetParent issue={issue} onDone={() => setSetting(false)} />}
      </dd>
    </div>
  )
}

/** The parent by its key, in any spelling; the project's other issues offered as the person types, but for the issue's own sub-issues, which would make a loop. */
function SetParent({ issue, onDone }: { issue: Issue; onDone: () => void }) {
  const store = useStore()
  const { issues } = useBoardState()
  const [typed, setTyped] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const listId = useId()
  const candidates = useMemo(() => {
    const below = new Set<string>([issue.id])
    for (let grew = true; grew; ) {
      grew = false
      for (const i of issues.values()) {
        if (i.parent && below.has(i.parent) && !below.has(i.id)) {
          below.add(i.id)
          grew = true
        }
      }
    }
    return [...issues.values()].filter((i) => i.project === issue.project && !below.has(i.id)).sort((a, b) => a.number - b.number)
  }, [issues, issue.id, issue.project])
  const submit = (event: FormEvent) => {
    event.preventDefault()
    const text = typed.trim()
    if (!text) return
    if (sameKey(text, issue.key)) {
      setProblem('An issue cannot be its own parent.')
      return
    }
    const parent = candidates.find((i) => sameKey(i.key, text))
    if (!parent) {
      setProblem(`No issue ${text} in this project, outside this issue's own sub-issues.`)
      return
    }
    void store.setParent(issue.id, parent.id)
    onDone()
  }
  return (
    <form className="relation-add" onSubmit={submit} aria-label="Set parent">
      <input
        aria-label="Parent"
        placeholder="Issue key, such as Poulpe-012"
        list={listId}
        value={typed}
        autoFocus
        onChange={(e) => {
          setTyped(e.target.value)
          setProblem(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            onDone()
          }
        }}
      />
      <datalist id={listId}>
        {candidates.map((candidate) => (
          <option key={candidate.id} value={candidate.key}>
            {candidate.title}
          </option>
        ))}
      </datalist>
      <button type="submit" className="button button-small" disabled={!typed.trim()}>
        Set
      </button>
      <button type="button" className="button button-small button-quiet" onClick={onDone}>
        Cancel
      </button>
      {problem && (
        <p className="relation-problem" role="alert">
          {problem}
        </p>
      )}
    </form>
  )
}

/** The Sub-issues section: each one's line with its state, and "2 of 5 done". Nothing when there are none. */
export function IssueSubIssues({ issue }: { issue: Issue }) {
  const { issues } = useBoardState()
  const children = useMemo(() => childrenOf(issues, issue.id), [issues, issue.id])
  if (children.length === 0) return null
  const progress = progressOf(children)
  const text = `${progress.done} of ${progress.total} done`
  return (
    <section className="section issue-subissues" aria-label="Sub-issues">
      <header className="section-header">
        <h2>
          Sub-issues<span className="section-count">{children.length}</span>
        </h2>
        <span className="progress" title={`Sub-issues: ${text}`}>
          <span className="progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done} aria-label="Sub-issues">
            <span className="progress-fill" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
          </span>
          <span className="progress-count">{text}</span>
        </span>
      </header>
      <ul className="line-list">
        {children.map((child) => (
          <li key={child.id}>
            <Link className="issue-line" to={issuePath(child)}>
              <StateIcon state={child.state} labelled />
              <span className="issue-line-key">{child.key}</span>
              <span className="issue-line-title">{child.title}</span>
              <PriorityIcon priority={child.priority} />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
